import React, { useState, useEffect, useCallback, memo, useContext } from 'react';
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Download,
  RefreshCw,
  Bell,
  Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext.jsx';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import debounce from 'lodash/debounce';
import { formatUTCTime } from '../utils/dateUtils';

const StatCard = memo(({ title, value, change, color, trend }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 md:p-6 hover:shadow-md transition-all duration-300 group">
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-xs md:text-sm font-medium text-gray-600 mb-1 leading-tight">{title}</p>
        <p className="text-lg md:text-2xl font-bold text-gray-900 mb-1 md:mb-2">{value}</p>
        {change && (
          <div className={`flex items-center text-xs md:text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-3 h-3 md:w-4 md:h-4 mr-1 flex-shrink-0" /> : <ArrowDownRight className="w-3 h-3 md:w-4 md:h-4 mr-1 flex-shrink-0" />}
            <span className="truncate">{change}</span>
          </div>
        )}
      </div>
      <div className={`w-8 h-8 md:w-12 md:h-12 rounded-lg flex items-center justify-center ${color} group-hover:scale-110 transition-transform duration-300 flex-shrink-0 ml-2`}>
        <span className="text-white font-bold text-xs md:text-base">{title.charAt(0)}</span>
      </div>
    </div>
  </div>
));

const QuickAction = memo(({ title, color, onClick }) => (
  <button
    onClick={onClick}
    className={`p-3 md:p-4 rounded-xl ${color} text-white hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}
  >
    <div className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2 rounded-full bg-white/30"></div>
    <p className="text-xs md:text-sm font-medium leading-tight">{title}</p>
  </button>
));

// Skeleton Components for Loading State
const SkeletonStatCard = memo(() => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-3 bg-gray-100 rounded w-20"></div>
      </div>
      <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
    </div>
  </div>
));

const SkeletonChart = memo(() => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div className="h-5 bg-gray-200 rounded w-40"></div>
      <div className="flex space-x-2">
        <div className="h-6 bg-gray-100 rounded-full w-14"></div>
        <div className="h-6 bg-gray-100 rounded-full w-12"></div>
      </div>
    </div>
    <div className="h-[300px] bg-gray-100 rounded-lg flex items-center justify-center">
      <div className="text-gray-300">
        <TrendingUp className="w-12 h-12" />
      </div>
    </div>
  </div>
));

const SkeletonPieChart = memo(() => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
    <div className="h-5 bg-gray-200 rounded w-40 mb-6"></div>
    <div className="h-[300px] flex items-center justify-center">
      <div className="w-40 h-40 rounded-full bg-gray-200"></div>
    </div>
    <div className="mt-4 space-y-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-gray-200 mr-2"></div>
            <div className="h-3 bg-gray-100 rounded w-16"></div>
          </div>
          <div className="h-3 bg-gray-200 rounded w-8"></div>
        </div>
      ))}
    </div>
  </div>
));

const SkeletonTransactionList = memo(() => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div className="h-5 bg-gray-200 rounded w-40"></div>
      <div className="h-4 bg-gray-100 rounded w-16"></div>
    </div>
    <div className="space-y-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-200 mr-3"></div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
              <div className="h-3 bg-gray-100 rounded w-36"></div>
            </div>
          </div>
          <div className="text-right">
            <div className="h-4 bg-gray-200 rounded w-16 mb-2 ml-auto"></div>
            <div className="h-3 bg-gray-100 rounded w-12 ml-auto"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
));

const SkeletonLowStock = memo(() => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center">
        <div className="w-5 h-5 rounded-full bg-gray-200 mr-2"></div>
        <div className="h-5 bg-gray-200 rounded w-32"></div>
      </div>
      <div className="h-4 bg-gray-100 rounded w-24"></div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-100 rounded w-1/2 mb-3"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="h-6 bg-gray-200 rounded w-8"></div>
            <div className="h-3 bg-gray-100 rounded w-12"></div>
          </div>
          <div className="h-2 bg-gray-200 rounded-full w-full"></div>
        </div>
      ))}
    </div>
  </div>
));

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [dateTime] = useState({
    date: new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
    time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
  });

  // Helper to get correct auth token
  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('authToken');
  };

  const [refreshing, setRefreshing] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [error, setError] = useState(null);

  const [summary, setSummary] = useState({
    todaySales: 0,
    monthlyRevenue: 0,
    lowStock: 0,
    staffOnDuty: 0,
    expenses: 0,
    netProfit: 0,
    inventoryCost: 0,
    vatPayable: 0,
    customersToday: 0,
    averageTransaction: 0,
    topService: 'N/A',
  });

  const [salesData, setSalesData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [serviceDistribution, setServiceDistribution] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

  const getAuthHeaders = useCallback(() => {
    const token = getAuthToken();
    if (!token) throw new Error('No auth token found');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchTodayTransactions = useCallback(async () => {
    // Get today's date in local timezone
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const response = await fetch(`${API_BASE_URL}/api/pos-transactions?startDate=${todayStr}&endDate=${todayStr}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to fetch today\'s transactions');
    }
    return await response.json();
  }, [getAuthHeaders, API_BASE_URL]);

  const fetchMonthlyTransactions = useCallback(async () => {
    // Get current month data
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    const startDateStr = startOfMonth.toISOString().split('T')[0];
    const endDateStr = endOfMonth.toISOString().split('T')[0];

    const response = await fetch(`${API_BASE_URL}/api/pos-transactions?startDate=${startDateStr}&endDate=${endDateStr}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to fetch monthly transactions');
    }
    return await response.json();
  }, [getAuthHeaders, API_BASE_URL]);

  const fetchSixMonthsTransactions = useCallback(async () => {
    // Get last 6 months of data
    const today = new Date();
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 5, 1);
    
    const startDateStr = sixMonthsAgo.toISOString().split('T')[0];
    const endDateStr = today.toISOString().split('T')[0];

    const response = await fetch(`${API_BASE_URL}/api/pos-transactions?startDate=${startDateStr}&endDate=${endDateStr}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to fetch historical transactions');
    }
    return await response.json();
  }, [getAuthHeaders, API_BASE_URL]);

  const fetchInventory = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/api/inventory`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to fetch inventory');
    }
    return await response.json();
  }, [getAuthHeaders, API_BASE_URL]);

  const fetchTodayAttendance = useCallback(async () => {
    const today = new Date().toISOString().split('T')[0];
    const response = await fetch(`${API_BASE_URL}/api/attendance/report?startDate=${today}&endDate=${today}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch attendance');
    return await response.json();
  }, [getAuthHeaders, API_BASE_URL]);

  const fetchRecentAttendanceLogs = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/api/attendance/live?limit=5`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch recent attendance');
    return await response.json();
  }, [getAuthHeaders, API_BASE_URL]);

  const fetchExpenses = useCallback(async () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

    const response = await fetch(`${API_BASE_URL}/api/expenses?startDate=${startOfMonth}&endDate=${endOfMonth}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to fetch expenses');
    }
    return await response.json();
  }, [getAuthHeaders, API_BASE_URL]);

  const getTopService = useCallback((transactions) => {
    const serviceCounts = {};
    transactions.forEach((t) => {
      if (t.items) {
        t.items.forEach((item) => {
          serviceCounts[item.name] = (serviceCounts[item.name] || 0) + item.quantity;
        });
      }
    });

    const topService = Object.entries(serviceCounts).sort((a, b) => b[1] - a[1])[0];
    return topService ? topService[0] : 'N/A';
  }, []);

  const processSalesDataByHour = useCallback((transactions) => {
    const hourlyData = {};
    const hours = ['9AM', '10AM', '11AM', '12PM', '1PM', '2PM', '3PM', '4PM', '5PM', '6PM'];

    hours.forEach((hour) => {
      hourlyData[hour] = { time: hour, salon: 0, spa: 0, gym: 0 };
    });

    transactions.forEach((t) => {
      const hour = new Date(t.createdAt).getHours();
      const timeKey = hour >= 12 ? `${hour === 12 ? 12 : hour - 12}PM` : `${hour}AM`;

      if (hourlyData[timeKey]) {
        // ✅ Handle array format for subBusiness
        const subBusinessArray = Array.isArray(t.subBusiness) ? t.subBusiness : [t.subBusiness];
        const amount = t.totalAmount || 0;

        if (subBusinessArray.length === 1) {
          // Single sub-business: add full amount
          const subBusiness = subBusinessArray[0] ? subBusinessArray[0].toLowerCase() : 'salon';
          hourlyData[timeKey][subBusiness] = (hourlyData[timeKey][subBusiness] || 0) + amount;
        } else {
          // Multiple sub-businesses: split proportionally by items
          const itemsBySubBusiness = {};
          let totalItemValue = 0;

          (t.items || []).forEach(item => {
            const itemSubBusiness = (item.subBusiness || 'salon').toLowerCase();
            const itemValue = (item.price || 0) * (item.quantity || 1);

            if (!itemsBySubBusiness[itemSubBusiness]) {
              itemsBySubBusiness[itemSubBusiness] = 0;
            }
            itemsBySubBusiness[itemSubBusiness] += itemValue;
            totalItemValue += itemValue;
          });

          // Distribute amount proportionally
          Object.entries(itemsBySubBusiness).forEach(([subBiz, itemValue]) => {
            if (totalItemValue > 0) {
              const proportion = itemValue / totalItemValue;
              hourlyData[timeKey][subBiz] = (hourlyData[timeKey][subBiz] || 0) + (amount * proportion);
            }
          });
        }
      }
    });

    return Object.values(hourlyData);
  }, []);

  const processServiceDistribution = useCallback((transactions) => {
    const distribution = { salon: 0, spa: 0, gym: 0, products: 0 };

    transactions.forEach((t) => {
      // ✅ Handle array format for subBusiness
      const subBusinessArray = Array.isArray(t.subBusiness) ? t.subBusiness : [t.subBusiness];
      const amount = t.totalAmount || 0;

      if (t.type === 'product') {
        distribution.products += amount;
      } else if (subBusinessArray.length === 1) {
        // Single sub-business: add full amount
        const subBusiness = subBusinessArray[0] ? subBusinessArray[0].toLowerCase() : 'salon';
        distribution[subBusiness] = (distribution[subBusiness] || 0) + amount;
      } else {
        // Multiple sub-businesses: split proportionally by items
        const itemsBySubBusiness = {};
        let totalItemValue = 0;

        (t.items || []).forEach(item => {
          const itemSubBusiness = (item.subBusiness || 'salon').toLowerCase();
          const itemValue = (item.price || 0) * (item.quantity || 1);

          if (!itemsBySubBusiness[itemSubBusiness]) {
            itemsBySubBusiness[itemSubBusiness] = 0;
          }
          itemsBySubBusiness[itemSubBusiness] += itemValue;
          totalItemValue += itemValue;
        });

        // Distribute amount proportionally
        Object.entries(itemsBySubBusiness).forEach(([subBiz, itemValue]) => {
          if (totalItemValue > 0) {
            const proportion = itemValue / totalItemValue;
            distribution[subBiz] = (distribution[subBiz] || 0) + (amount * proportion);
          }
        });
      }
    });

    const total = Object.values(distribution).reduce((sum, val) => sum + val, 0);

    return [
      { name: 'Hair Services', value: total > 0 ? Math.round((distribution.salon / total) * 100) : 0, color: '#8B5CF6' },
      { name: 'Spa Treatments', value: total > 0 ? Math.round((distribution.spa / total) * 100) : 0, color: '#06D6A0' },
      { name: 'Gym Memberships', value: total > 0 ? Math.round((distribution.gym / total) * 100) : 0, color: '#FFD23F' },
      { name: 'Products', value: total > 0 ? Math.round((distribution.products / total) * 100) : 0, color: '#F72585' },
    ];
  }, []);

  const processRevenueDataFromSixMonths = useCallback((sixMonthsTransactions) => {
    // Process last 6 months of data
    const months = [];
    const today = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth();
      
      // Filter transactions for this month
      const monthTransactions = sixMonthsTransactions.filter(t => {
        const transactionDate = new Date(t.createdAt);
        return transactionDate.getFullYear() === year && transactionDate.getMonth() === month;
      });
      
      const revenue = monthTransactions.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
      
      months.push({
        month: monthName,
        revenue,
        expenses: revenue * 0.3, // Estimated expenses (30% of revenue)
        profit: revenue * 0.7   // Estimated profit (70% of revenue)
      });
    }
    
    return months;
  }, []);

  const processSummaryData = useCallback(async () => {
    try {
      setLoadingSummary(true);
      const [todayTransactions, monthlyTransactions, inventory, expenses, attendanceData, recentAttendanceLogs] = await Promise.all([
        fetchTodayTransactions(),
        fetchMonthlyTransactions(),
        fetchInventory(),
        fetchExpenses(),
        fetchTodayAttendance(),
        fetchRecentAttendanceLogs(),
      ]);

      // Process today's data
      const todaysSales = todayTransactions.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
      const customersToday = new Set(todayTransactions.map((t) => t.customer?.name || t.name || 'Guest')).size;
      const averageTransaction = customersToday > 0 ? todaysSales / customersToday : 0;
      
      // Process monthly data
      const monthlyRevenue = monthlyTransactions.reduce((sum, t) => sum + (t.totalAmount || 0), 0);
      
      // Process inventory
      const lowStock = inventory.filter((item) => item.reorderPoint && item.quantity <= item.reorderPoint);
      const inventoryCost = inventory.reduce((sum, item) => sum + ((item.purchasePrice || 0) * (item.quantity || 0)), 0);
      
      // Process expenses
      const monthlyExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      
      // Process attendance
      const staffOnDuty = attendanceData.attendances?.filter(a => a.checkIn && !a.absent).length || 0;
      
      // Set recent attendance logs
      setRecentAttendance(recentAttendanceLogs.logs || []);

      setSummary({
        todaySales: todaysSales,
        monthlyRevenue: monthlyRevenue,
        lowStock: lowStock.length,
        staffOnDuty: staffOnDuty,
        expenses: monthlyExpenses,
        netProfit: monthlyRevenue - monthlyExpenses,
        inventoryCost: inventoryCost,
        vatPayable: todaysSales * 0.075,
        customersToday: customersToday,
        averageTransaction: averageTransaction,
        topService: getTopService(todayTransactions),
      });

      return { todayTransactions, monthlyTransactions, inventory, attendanceData };
    } catch (error) {
      setError(error.message || 'Failed to load summary data');
      throw error;
    } finally {
      setLoadingSummary(false);
    }
  }, [getTopService, fetchTodayTransactions, fetchMonthlyTransactions]);

  const processChartData = useCallback(async ({ todayTransactions, monthlyTransactions, inventory, attendanceData }) => {
    try {
      setLoadingCharts(true);
      
      // Fetch 6 months data for revenue trends
      const sixMonthsTransactions = await fetchSixMonthsTransactions();
      
      setSalesData(processSalesDataByHour(todayTransactions));
      setServiceDistribution(processServiceDistribution(todayTransactions));
      setRecentTransactions(
        todayTransactions
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5)
          .map((t) => {
            // ✅ Handle array format for subBusiness
            const subBusinessArray = Array.isArray(t.subBusiness) ? t.subBusiness : [t.subBusiness];
            const displaySubBusiness = subBusinessArray.length === 1
              ? (subBusinessArray[0] || 'salon').toLowerCase()
              : subBusinessArray.map(s => s.toLowerCase()).join('_'); // e.g., "salon_spa"

            // Determine customer display name
            let customerName = t.customer?.name || 'Walk-in Customer';
            // If no customer object, check if t.name looks like a transaction ID (e.g., "Sale - 2025-12-24T...")
            if (!t.customer?.name && t.name && (t.name.startsWith('Sale -') || t.name.includes('T') && t.name.includes(':'))) {
              customerName = 'Walk-in Customer';
            } else if (!t.customer?.name && t.name) {
              customerName = t.name;
            }

            return {
              id: t._id,
              customer: customerName,
              service: t.items ? t.items.map((i) => i.name).join(', ') : 'Service',
              amount: t.totalAmount || 0,
              time: new Date(t.createdAt).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),
              type: displaySubBusiness,
            };
          }),
      );
      setLowStockItems(
        inventory
          .filter((item) => item.reorderPoint && item.quantity <= item.reorderPoint)
          .slice(0, 5)
          .map((item) => ({
            name: item.name,
            quantity: item.quantity,
            threshold: item.reorderPoint,
            category: item.category,
          })),
      );
      
      // Set revenue data from 6 months transactions
      setRevenueData(processRevenueDataFromSixMonths(sixMonthsTransactions));
      
    } catch (error) {
      setError(error.message || 'Failed to load chart data');
    } finally {
      setLoadingCharts(false);
    }
  }, [processSalesDataByHour, processServiceDistribution, processRevenueDataFromSixMonths, fetchSixMonthsTransactions]);

  useEffect(() => {
    processSummaryData().then(processChartData).catch(() => {});
  }, [processSummaryData, processChartData]);

  const handleRefresh = useCallback(
    debounce(() => {
      setRefreshing(true);
      processSummaryData()
        .then(processChartData)
        .finally(() => setRefreshing(false));
    }, 1000),
    [processSummaryData, processChartData]
  );

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 font-bold">!</span>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="px-4 py-3 md:px-8 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 pl-10 md:pl-0">
              <div className="w-9 h-9 md:w-12 md:h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm md:text-lg">E</span>
              </div>
              <div>
                <h1 className="text-base md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
                  Ellas Place
                </h1>
                <p className="text-gray-600 text-xs md:text-sm hidden sm:block">Salon • Spa • Ladies Gym</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 md:space-x-6">
              <div className="text-right hidden md:block">
                <p className="text-lg font-semibold text-gray-900">{dateTime.time}</p>
                <p className="text-sm text-gray-600">{dateTime.date}</p>
              </div>

              <div className="flex items-center space-x-2 md:space-x-3">
                <button
                  onClick={handleRefresh}
                  className={`p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors ${refreshing ? 'animate-spin' : ''}`}
                >
                  <RefreshCw className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                </button>
                <button className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors relative">
                  <Bell className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                  {summary.lowStock > 0 && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="px-4 py-4 md:px-8 md:py-6">
        {loadingSummary ? (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            {[...Array(4)].map((_, i) => <SkeletonStatCard key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            <StatCard
              title="Today's Revenue"
              value={`₦${(summary.todaySales / 1000).toFixed(0)}K`}
              change="+12.5%"
              trend="up"
              color="bg-gradient-to-r from-green-500 to-emerald-600"
            />
            <StatCard
              title="Active Customers"
              value={summary.customersToday.toString()}
              change="+8.2%"
              trend="up"
              color="bg-gradient-to-r from-blue-500 to-cyan-600"
            />
            <StatCard
              title="Staff on Duty"
              value={summary.staffOnDuty.toString()}
              change="All present"
              trend="up"
              color="bg-gradient-to-r from-purple-500 to-violet-600"
            />
            <StatCard
              title="Low Stock Alerts"
              value={summary.lowStock.toString()}
              change={summary.lowStock > 0 ? `${summary.lowStock} items need restocking` : 'All good'}
              trend={summary.lowStock > 0 ? 'down' : 'up'}
              color="bg-gradient-to-r from-orange-500 to-red-600"
            />
          </div>
        )}

        <div className="mb-6 md:mb-8">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Quick Actions</h2>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-4">
            <QuickAction title="New Sale" color="bg-gradient-to-r from-green-500 to-emerald-600" onClick={() => navigate('/pos')} />
            <QuickAction title="Add Customer" color="bg-gradient-to-r from-blue-500 to-cyan-600" onClick={() => navigate('/customers')} />
            <QuickAction title="Check Inventory" color="bg-gradient-to-r from-purple-500 to-violet-600" onClick={() => navigate('/inventory')} />
            <QuickAction
              title="Staff Attendance"
              color="bg-gradient-to-r from-orange-500 to-red-600"
              onClick={() => navigate('/attendance')}
            />
            <QuickAction title="View Reports" color="bg-gradient-to-r from-pink-500 to-rose-600" onClick={() => navigate('/reports')} />
            <QuickAction title="Settings" color="bg-gradient-to-r from-indigo-500 to-blue-600" onClick={() => navigate('/settings')} />
          </div>
        </div>

        {loadingCharts ? (
          <>
            {/* Skeleton Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-4 md:mb-8">
              <div className="lg:col-span-2">
                <SkeletonChart />
              </div>
              <SkeletonPieChart />
            </div>

            {/* Skeleton Revenue Trends */}
            <div className="mb-4 md:mb-8">
              <SkeletonChart />
            </div>

            {/* Skeleton Transactions & Attendance */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
              <SkeletonTransactionList />
              <SkeletonTransactionList />
            </div>

            {/* Skeleton Low Stock */}
            <SkeletonLowStock />
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-8 mb-4 md:mb-8">
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h3 className="text-sm md:text-lg font-semibold text-gray-900">Today's Revenue</h3>
                  <div className="flex space-x-1 md:space-x-2">
                    <button className="px-2 md:px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">Today</button>
                    <button className="px-2 md:px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">Week</button>
                    <button className="px-2 md:px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">Month</button>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={salesData}>
                    <defs>
                      <linearGradient id="salonGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="spaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06D6A0" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#06D6A0" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="gymGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FFD23F" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#FFD23F" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="time" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="salon" stackId="1" stroke="#8B5CF6" fill="url(#salonGradient)" name="Salon" />
                    <Area type="monotone" dataKey="spa" stackId="1" stroke="#06D6A0" fill="url(#spaGradient)" name="Spa" />
                    <Area type="monotone" dataKey="gym" stackId="1" stroke="#FFD23F" fill="url(#gymGradient)" name="Gym" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
                <h3 className="text-sm md:text-lg font-semibold text-gray-900 mb-4 md:mb-6">Service Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={serviceDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                      {serviceDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {serviceDistribution.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }}></div>
                        <span className="text-sm text-gray-600">{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-4 md:mb-8 bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h3 className="text-sm md:text-lg font-semibold text-gray-900">Monthly Revenue Trends</h3>
                <div className="flex space-x-1 md:space-x-2">
                  <button className="px-2 md:px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">6 Months</button>
                  <button className="px-2 md:px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">Year</button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#8B5CF6" strokeWidth={2} name="Revenue" />
                  <Line type="monotone" dataKey="expenses" stroke="#F72585" strokeWidth={2} name="Expenses" />
                  <Line type="monotone" dataKey="profit" stroke="#06D6A0" strokeWidth={2} name="Profit" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h3 className="text-sm md:text-lg font-semibold text-gray-900">Recent Transactions</h3>
                  <button className="text-purple-600 hover:text-purple-700 text-sm font-medium" onClick={() => navigate('/pos')}>
                    View All
                  </button>
                </div>
                <div className="space-y-4">
                  {recentTransactions.length > 0 ? (
                    recentTransactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                      >
                        <div className="flex items-center">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                              transaction.type === 'salon' ? 'bg-purple-100' : transaction.type === 'spa' ? 'bg-green-100' : 'bg-yellow-100'
                            }`}
                          >
                            <span
                              className={`text-sm font-medium ${
                                transaction.type === 'salon' ? 'text-purple-600' : transaction.type === 'spa' ? 'text-green-600' : 'text-yellow-600'
                              }`}
                            >
                              {transaction.customer.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{transaction.customer}</p>
                            <p className="text-sm text-gray-600">{transaction.service}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {transaction.amount >= 1000
                              ? `₦${Math.round(transaction.amount / 1000)}K`
                              : `₦${transaction.amount.toLocaleString()}`
                            }
                          </p>
                          <p className="text-sm text-gray-600">{transaction.time}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <div className="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-2 flex items-center justify-center">
                        <span className="text-gray-500 font-bold">T</span>
                      </div>
                      <p>No transactions today</p>
                    </div>
                  )}
                </div>
              </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h3 className="text-sm md:text-lg font-semibold text-gray-900">Recent Clock-ins</h3>
                <button className="text-purple-600 hover:text-purple-700 text-sm font-medium" onClick={() => navigate('/attendance')}>
                  View All
                </button>
              </div>
              <div className="space-y-4">
                {recentAttendance.length > 0 ? (
                  recentAttendance.map((attendance) => (
                    <div key={attendance._id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-3">
                          <Clock className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{attendance.staffName}</p>
                          <p className="text-sm text-gray-600">
                            {attendance.position} • {attendance.department}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center mb-1">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            attendance.determinedType === 'clock-in' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {attendance.determinedType === 'clock-in' ? 'In' : 'Out'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {formatUTCTime(attendance.timestamp)}
                        
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-2 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-gray-500" />
                    </div>
                    <p>No recent attendance logs</p>
                  </div>
                )}
              </div>
            </div>
            </div>

            {lowStockItems.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <div className="flex items-center">
                    <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-orange-500 mr-2"></div>
                    <h3 className="text-sm md:text-lg font-semibold text-gray-900">Low Stock Alerts</h3>
                  </div>
                  <button className="text-orange-600 hover:text-orange-700 text-xs md:text-sm font-medium" onClick={() => navigate('/inventory')}>
                    Restock Items
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                  {lowStockItems.map((item, index) => (
                    <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 text-sm">{item.name}</h4>
                        <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{item.category}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-orange-600">{item.quantity}</span>
                        <span className="text-xs text-gray-500">of {item.threshold}</span>
                      </div>
                      <div className="w-full bg-orange-200 rounded-full h-2 mt-2">
                        <div
                          className="bg-orange-500 h-2 rounded-full"
                          style={{ width: `${Math.min((item.quantity / item.threshold) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <footer className="bg-white border-t border-gray-200 px-4 py-4 md:px-8">
        <div className="text-center">
          <p className="text-gray-600 text-sm">
            © 2025 Ellas Place. All rights reserved. Powered by{' '}
            <a href="https://www.opsuite.io" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 font-medium">
              Opsuite.io
            </a>{' '}
            <a href="tel:09052463435" className="text-gray-500 hover:text-gray-700">
              09052463435
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default memo(Dashboard);